import React from "react";
import { FunnelBarData, FunnelChartState } from "@/components/Funnel/types";
import { cn } from "@/lib/utils";

const BAR_HEIGHT = 40;
const WEDGE_HEIGHT = 16;
const BORDER_RADIUS = 6;

type SIDE = "left" | "right";

export const HalfFunnel = ({
  stages,
  color,
  side,
  onClick,
  selectedState = null,
  fade = false,
  getBadge,
  wedgeClassName = "",
  wedgeColor,
  percentColor,
  countColor,
  bgColor,
}: {
  stages: FunnelBarData[];
  color: string;
  side: SIDE;
  hideBadge?: boolean;
  selectedState: FunnelChartState | null;
  wedgeClassName: string; // TODO: remove?
  wedgeColor: string;
  percentColor: string;
  countColor: string;
  bgColor?: string;
  fade?: boolean;
  onClick?: (stage: FunnelChartState) => void;
  getBadge?: (
    percent: number | undefined,
    count: number,
    isFaded: boolean,
    percentColor: string,
    countColor: string,
    bgColor?: string,
  ) => React.ReactNode;
}) => {
  // need to keep track of previous bar width in order to calculate width of current one
  let prevWidthFraction = 1;

  return (
    <div className={cn(side === "left" && "justify-end")}>
      {stages.map((stage, idx) => {
        const isLast = idx === stages.length - 1;
        const percent = (stage.percent || 0) * prevWidthFraction;
        prevWidthFraction = percent / 100;
        const isFaded = fade && selectedState !== stage.stage;

        const nextPercentage = isLast
          ? 0
          : (stages[idx + 1].percent || 0) * prevWidthFraction;
        return (
          <div
            key={idx} // actually want index as key here since we don't want React to rearrange bars if values change
            style={{ width: `100%` }}
            className={cn(
              "flex flex-col transition-none",
              side === "left" && "items-end",
            )}
          >
            <div
              className={cn(
                `h-[${BAR_HEIGHT}px] flex w-full opacity-100 duration-300`,
                side === "left" && "justify-end",
              )}
            >
              {side === "left" && (
                <div>
                  <div className="relative">
                    {getBadge && (
                      <div
                        className={cn(
                          `absolute top-1 right-2`,
                          onClick && "cursor-pointer",
                        )}
                        data-testid={`funnel-badge-left-${stage.stage}`}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          return onClick && onClick(stage.stage);
                        }}
                      >
                        {getBadge(
                          stage.percent,
                          stage.count,
                          isFaded,
                          percentColor,
                          countColor,
                          bgColor,
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
              <FunnelBar
                side={side}
                isFaded={isFaded}
                stage={stage.stage}
                percentWidth={percent}
                barColor={color}
                onClick={onClick}
              />
              {side === "right" && (
                <div className="relative">
                  {getBadge && (
                    <div
                      className={cn(
                        `absolute top-1 left-2`,
                        onClick && "cursor-pointer",
                      )}
                      data-testid={`funnel-badge-right-${stage.stage}`}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        return onClick && onClick(stage.stage);
                      }}
                    >
                      {getBadge(
                        stage.percent,
                        stage.count,
                        isFaded,
                        percentColor,
                        countColor,
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Trapezoid / wedge */}
            {!isLast && (
              <FunnelWedge
                side={side}
                percent={percent}
                nextPercentage={nextPercentage}
                wedgeClassName={wedgeClassName}
                wedgeColor={wedgeColor}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

const FunnelBar = ({
  side,
  isFaded,
  stage,
  percentWidth,
  barColor,
  onClick,
}: {
  side: SIDE;
  isFaded: boolean;
  stage: FunnelChartState;
  percentWidth: number;
  barColor: string;
  onClick?: (stage: FunnelChartState) => void;
}) => (
  <div
    className={cn(
      `h-[${BAR_HEIGHT}px] w-full min-w-[1px] opacity-100 transition-all duration-300 ease-out`,
      isFaded && "opacity-30",
      side === "right" ? "rounded-r-md" : "rounded-l-md",
      onClick && "cursor-pointer",
    )}
    data-testid={`funnel-bar-${side}-${stage}`}
    onClick={(event) => {
      event.preventDefault();
      event.stopPropagation();
      return onClick && onClick(stage);
    }}
    style={{
      width: `${percentWidth}%`,
      backgroundColor: barColor,
    }}
  ></div>
);

const FunnelWedge = ({
  side,
  percent,
  nextPercentage,
  wedgeClassName,
  wedgeColor,
}: {
  side: SIDE;
  percent: number;
  nextPercentage: number;
  wedgeClassName: string;
  wedgeColor: string;
}) => (
  <div
    style={{
      width: "100%",
      backgroundColor: wedgeColor,
      height: `${WEDGE_HEIGHT}px`,
      clipPath:
        side === "right"
          ? `polygon(0 0, calc(${percent}% - ${BORDER_RADIUS}px) 0, calc(${nextPercentage}% - ${BORDER_RADIUS}px) 100%, 0 100%)`
          : `polygon(calc(${100 - percent}% + ${BORDER_RADIUS}px) 0, 100% 0, 100% 100%, calc(${100 - nextPercentage}% + ${BORDER_RADIUS}px) 100%)`,
    }}
    className={`h-4 w-full ${wedgeClassName} transition-all duration-300`}
  ></div>
);
